import { useCallback, useContext, useEffect, useState } from "react";
import api from "../../utils/axiosInstance";
import { AuthContext } from "../../context/AuthContext";
import { toast } from "../Toast/toast";
import Button from "../Button/Button";
import AuthCheckbox from "../Auth/AuthCheckbox";
import AgreementModal from "./AgreementModal";

// Shown over the vendor portal while a newer agreement is waiting; the vendor
// either accepts it or logs out.
const VendorAgreementGate = () => {
  const { user, setUser, refreshUser } = useContext(AuthContext);
  const pending = user?.roleSlug === "vendor" && user?.vendor_flags?.agreement_pending;

  const [agreement, setAgreement] = useState(null);
  const [readToEnd, setReadToEnd] = useState(false);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!pending) return;
    api
      .get("/vendor-agreement")
      .then((res) => setAgreement(res.data.data))
      .catch(() => setAgreement(null));
  }, [pending]);

  const handleReadToEnd = useCallback(() => setReadToEnd(true), []);

  if (!pending || !agreement) return null;

  const handleAccept = async () => {
    setSaving(true);
    try {
      await api.post("/vendor-agreement/accept");
      await refreshUser();
      toast.success("Vendor agreement accepted.");
      // Screens that loaded while the agreement was pending were refused.
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not accept the agreement.");
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    setLeaving(true);
    try {
      await api.post("auth/logout");
    } finally {
      setUser(null);
      window.location.assign("/");
    }
  };

  const updated = Boolean(agreement.accepted_at);

  return (
    <AgreementModal
      title={updated ? "Our Vendor Agreement Has Been Updated" : "Vendor Agreement"}
      intro={
        updated
          ? "Please review the updated agreement. You need to accept it to continue using the vendor portal."
          : "Please review the agreement. You need to accept it to continue using the vendor portal."
      }
      html={agreement.text}
      url={agreement.url}
      updatedAt={agreement.updated_at}
      onReadToEnd={handleReadToEnd}
      footer={
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <AuthCheckbox
              name="agreement_accepted"
              checked={checked}
              onChange={(e) => readToEnd && setChecked(e.target.checked)}
            >
              <span className={readToEnd ? "text-gray-700" : "text-gray-400"}>
                I have read and accept the Vendor Agreement.
              </span>
            </AuthCheckbox>
            {!readToEnd && (
              <p className="ml-[26px] mt-0.5 text-[11px] text-gray-400">
                Scroll to the end of the agreement to continue.
              </p>
            )}
          </div>
          <div className="flex shrink-0 gap-2">
            <Button variant="secondary" size="sm" onClick={handleLogout} disabled={leaving || saving}>
              {leaving ? "Logging out..." : "Log out"}
            </Button>
            <Button size="sm" onClick={handleAccept} disabled={!checked || saving || leaving}>
              {saving ? "Accepting..." : "Accept and Continue"}
            </Button>
          </div>
        </div>
      }
    />
  );
};

export default VendorAgreementGate;
